import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SystemModuleEnum, WarrantyStatusEnum } from 'src/enumerators';
import { DashboardTypeEnum } from 'src/enumerators/dashboard.enum';
import { environment } from 'src/environments/environment';
import {
  CustomChart,
  DashboardContract, DashboardContractOrganizationData,
  DashboardOrganization, DashboardPath,
  DashboardRetroreflection, DashboardStock,
  MonthlyTaskData,
  ServiceOrderDashboard,
  ServiceOrderData,
  SignRegisteredDate, SignRegisteredPerYear,
  TeamDashCard,
  TeamDashModal
} from 'src/models';
import { RestApiService } from './rest-api.service';

@Injectable({
  providedIn: 'root'
})
export class DashboardService extends RestApiService {
  // Variáveis
  environmentEndpoint = environment.endpoint();

  private _urlOrganizationDashboard = '/dashboard/organization';
  private _urlOrganizationDataDashboard = '/dashboard/organization/data';
  private _urlContractDataDashboard = '/dashboard/contract/:id/data';
  private _urlInvestmentItemContractDashboard = '/dashboard/contract/:id/investment';
  private _urlRegisteredYearDashboard = '/dashboard/year-signalings';
  private _urlRegisteredDateDashboard = '/dashboard/signalings';
  private _urlPathsDashboard = '/dashboard/paths';
  private _urlRetroreflectionDashboard = '/dashboard/retroreflection';
  private _urlContractIdDashboard = '/dashboard/contract/:id';
  private _urlContractIdRegionDashboard = '/dashboard/contract/:id/regions';
  private _urlStockDashboard = '/stocked-signalings';
  private _urlTeamDashboard = '/teams/tasks/status';
  private _urlTeamDashboardCard = '/teams/tasks/status/week';
  private _urlServiceOrderDashboard = '/dashboard/service-orders';
  private _urlServiceOrderAmountDashboard = '/dashboard/service-orders/amount';
  private _urlTaskStatusDashboard = '/dashboard/tasks/status';
  private _urlDashboardProjects = '/dashboard/projects'
  private _urlDashboardEntitiesStatus = '/dashboard/entities-status'
  private _urlDashboardCatalog = '/dashboard/signalings'
  private _urlDashboardTeamTasks = '/teams/tasks'
  private _urlDashboardTasks = '/dashboard/tasks'
  private _urlDashboardServiceOrder = '/service-orders/tasks'

  private _urlCharts = '/charts'
  private _urlChartsId = '/charts/:id'

  constructor(public http: HttpClient) {
    super(http);
  }

  // GET /dashboard - retorna os dados da dashboard s/ queryParams
  getDashboardOrganization(startDate: Date, endDate: Date, optionsFilter?: any) {
    let url = this._urlOrganizationDashboard + '?';

    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, DashboardOrganization);
  }

  // GET /dashboard - retorna os dados da dashboard de consumo de organização
  getDashboardOrganizationData() {
    let url = this._urlOrganizationDataDashboard;

    return this.get(url, DashboardContractOrganizationData);
  }

  // GET /dashboard - retorna os dados da dashboard de consumo de organização
  getDashboardTaskData(contractId?, year?, optionsFilter?) {
    let url = this._urlTaskStatusDashboard + '?';
    if (contractId) url += `contractId=${contractId}&`;
    if (year) url += `year=${year}&`;

    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, MonthlyTaskData);
  }

  // GET /dashboard - retorna os dados da dashboard de consumo de organização
  getDashboardServiceOrder(contractId, startDate?, endDate?, optionsFilter?) {
    let url = this._urlServiceOrderDashboard + '?';
    if (contractId) url += `contractId=${contractId}&`;
    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;

    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, ServiceOrderDashboard);
  }

  // GET /dashboard - retorna os dados da dashboard de consumo de organização
  getDashboardServiceOrderDate(contractId, startDate?, endDate?, optionsFilter?) {
    let url = this._urlServiceOrderDashboard + '?';
    if (contractId) url += `contractId=${contractId}&`;
    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;

    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, ServiceOrderData);
  }

  // GET /dashboard - retorna os dados da dashboard de consumo de organização
  getDashboardServiceOrderAmount(listId: Array<string>) {
    let url = this._urlServiceOrderAmountDashboard + '?';
    let listJson = JSON.stringify(listId);
    url += `serviceOrderIds=${listJson}&`;
    return this.get(url, ServiceOrderData);
  }

  // GET /dashboard - retorna os dados da dashboard de consumo de contrato
  getDashboardContractData(contractId) {
    let url = this._urlContractDataDashboard.replace(':id', contractId);

    return this.get(url, DashboardContractOrganizationData);
  }


  getDashboardInvestmentItemContract(contractId, startDate?: Date, endDate?: Date, optionsFilter?: any) {
    let url = this.environmentEndpoint + this._urlInvestmentItemContractDashboard.replace(':id', contractId) + '?';

    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    url = this.createFilterUrl(url, optionsFilter);

    return this.http.get(url);
  }

  getDashboardContractId(contractId, startDate?: Date, endDate?: Date, optionsFilter?: any) {
    let url = this._urlContractIdDashboard.replace(':id', contractId) + '?';

    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, DashboardContract);
  }

  getDashboardPath(contractId?, startDate?: Date, endDate?: Date, optionsFilter?: any) {
    let url = this._urlPathsDashboard + '?' + (contractId ? `contractId=${contractId}&` : '');

    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, DashboardPath);
  }

  getDashboardRetroreflection(contractId?, optionsFilter?: any) {
    let url = this._urlRetroreflectionDashboard + '?' + (contractId ? `contractId=${contractId}&` : '');

    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, DashboardRetroreflection);
  }

  getDashboardStock(contractId?, startDate?: Date, endDate?: Date, optionsFilter?: any) {
    let url = this._urlStockDashboard + '?' + (contractId ? `contractId=${contractId}` : '');

    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, DashboardStock);
  }

  getDashboardContractIdRegions(contractId, startDate?: Date, endDate?: Date, listType?: any) {
    let url = this.environmentEndpoint + this._urlContractIdRegionDashboard.replace(':id', contractId) + '?';

    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    let listJson = JSON.stringify(listType);
    url += listType?.length ? `types=${listJson}&` : '';

    return this.http.get(url);
  }

  getSignRegisteredYear(year: number, contractId?: string, startDate?: Date, endDate?: Date, optionsFilter?: any) {
    const c = contractId ? `contractId=${contractId}&` : '';
    let url = this._urlRegisteredYearDashboard + `?` + c;
    if (!startDate && !endDate && year) url += `year=${year}&`;
    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, SignRegisteredPerYear);
  }

  getSignRegisteredDate(year: number, warrantyStatus?: Array<WarrantyStatusEnum>, contractId?: string, startDate?: Date, endDate?: Date, optionsFilter?: any) {
    const y = year ? `year=${year}&` : '';
    let listJson = JSON.stringify(warrantyStatus);
    const w = warrantyStatus?.length ? `warrantyStatus=${listJson}&` : '';
    const c = contractId ? `contractId=${contractId}&` : '';
    let url = this._urlRegisteredDateDashboard + '?' + y + w + c;
    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, SignRegisteredDate);
  }

  getDashboardProjects(contractId: string, queryParams?: string) {
    let url = this.environmentEndpoint + this._urlDashboardProjects + (contractId !== 'all' ? '?contractId=' + contractId : '')
    if (queryParams) url += queryParams
    return this.http.get(url)
  }

  getDashboardEntitiesStatus() {
    return this.http.get(this.environmentEndpoint + this._urlDashboardEntitiesStatus);
  }

  getDashboardTasks(contractId: string, queryParams?: string) {
    let url = this.environmentEndpoint + this._urlDashboardTasks + (contractId !== 'all' ? '?contractId=' + contractId : '')
    if (queryParams) url += queryParams
    return this.http.get(url)
  }

  getDashboardServiceOrders(contractId: string, queryParams?: string) {
    let url = this.environmentEndpoint + this._urlDashboardServiceOrder + (contractId !== 'all' ? '?contractId=' + contractId : '')
    if (queryParams) url += queryParams
    return this.http.get(url)
  }

  getDashboardCatalog(contractId: string, queryParams?: string) {
    let url = this.environmentEndpoint + this._urlDashboardCatalog + (contractId !== 'all' ? '?contractId=' + contractId : '')
    if (queryParams) url += queryParams
    return this.http.get(url)
  }

  getDashboardTeamTasks(contractId: string, queryParams?: string) {
    let url = this.environmentEndpoint + this._urlDashboardTeamTasks + (contractId !== 'all' ? '?contractId=' + contractId : '')
    if (queryParams) url += queryParams
    return this.http.get(url)
  }

  getDashboardPendencies(contractId: string, queryParams?: string) {
    let url = this.environmentEndpoint + this._urlDashboardCatalog + `?status=${JSON.stringify(['PENDING'])}` + (contractId !== 'all' ? '&contractId=' + contractId : '')
    if (queryParams) url += queryParams
    return this.http.get(url)
  }

  createFilterUrl(url, optionsFilter) {
    let listJson;

    if (optionsFilter?.length) {
      optionsFilter.forEach((filter: any) => {

        if (filter.selected.length > 0) {
          switch (filter.value) {
            case SystemModuleEnum.Project:
              listJson = JSON.stringify(filter.selected);
              url += `projectIds=${listJson}&`;
              break;

            case SystemModuleEnum.Region:
              if (filter.selected[0] == '0') {
                url += `noRegions=true&`;
              } else {
                listJson = JSON.stringify(filter.selected);
                url += `regionIds=${listJson}&`;
              }
              break;

            case SystemModuleEnum.Area:
              if (filter.selected[0] == '0') {
                url += `noAreas=true&`;
              } else {
                listJson = JSON.stringify(filter.selected);
                url += `areaIds=${listJson}&`;
              }
              break;

            case DashboardTypeEnum.RetroAlert:
              url += `belowMinimumRetroreflection=${!!filter.selected.length}&`;
              break;

            case SystemModuleEnum.Warranty:
              listJson = JSON.stringify(filter.selected);
              url += `warrantyStatus=${listJson}&`;
              break;

            case SystemModuleEnum.SignalingRegister:
              listJson = JSON.stringify(filter.selected);
              url += `types=${listJson}&`;
              break;

            case SystemModuleEnum.Task:
              listJson = JSON.stringify(filter.selected);
              url += `taskIds=${listJson}&`;
              break;

            case SystemModuleEnum.Team:
              listJson = JSON.stringify(filter.selected);
              url += `teamIds=${listJson}&`;
              break;

            case SystemModuleEnum.ServiceOrder:
              listJson = JSON.stringify(filter.selected);
              url += `serviceOrderIds=${listJson}&`;
              break;


            default:
              break;
          }
        }
      })
    }
    return url;
  }

  getTeamDashboardCard(contractId) {
    let url = this._urlTeamDashboardCard + '?';
    if (contractId) url += `contractId=${contractId}&`;

    return this.get(url, TeamDashCard);
  }

  getTeamDashboard(contractId?, startDate?, endDate?, optionsFilter?) {
    let url = this._urlTeamDashboard + '?';
    if (contractId) url += `contractId=${contractId}&`;
    if (startDate) url += `startDate=${startDate.toISOString()}&`;
    if (endDate) url += `endDate=${endDate.toISOString()}&`;
    url = this.createFilterUrl(url, optionsFilter);

    return this.get(url, TeamDashModal);
  }

  createCustomChart(chartData: CustomChart) {
    const url = this.environmentEndpoint + this._urlCharts
    return this.http.post(url, chartData)
  }

  getCustomCharts() {
    const url = this.environmentEndpoint + this._urlCharts
    return this.http.get(url)
  }

  getCustomChartById(chartId: string) {
    const url = this.environmentEndpoint + this._urlCharts + `/${chartId}`
    return this.http.get(url)
  }

  editCustomChart(chartId: string, chartData) {
    const url = this.environmentEndpoint + this._urlCharts + `/${chartId}`
    return this.http.put(url, chartData)
  }

  deleteCustomChart(chartId: string) {
    const url = this.environmentEndpoint + this._urlCharts + `/${chartId}`
    return this.http.delete(url)
  }

}
